import { OptionNameValuePair } from "@/src/lib/interfaces/types";
import { Check, ChevronDown, ChevronUp, Search } from "lucide-react";
import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { cn } from "../../../../lib/utils";

interface SelectDropdownProps<T> {
  name: string;
  placeholder?: string;
  options: OptionNameValuePair<T>[];
  selectedOption?: OptionNameValuePair<T>;
  selectedContainerClassName?: string;
  onSelectionChange: (selectedOption: OptionNameValuePair<T>) => void;
  enableSearch?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  focusClassName?: string;
  disabled?: boolean;
  position?: "top" | "bottom";
  hideChevron?: boolean;
  showTitleSubTitle?: boolean;
  titleClassName?: string;
  subTitleClassName?: string;
  showTooltip?: boolean;
  footer?: React.ReactNode;
  dropdownClassName?: string;
  hideDisabledOptions?: boolean;
  optionClassName?: string; 
  showOptionTooltip?: boolean;
}

function SelectDropdownV2<T>({
  name,
  placeholder,
  options,
  selectedOption,
  selectedContainerClassName,
  onSelectionChange,
  enableSearch = true,
  containerClassName,
  labelClassName,
  focusClassName,
  disabled = false,
  position = "bottom",
  hideChevron = false,
  showTitleSubTitle = false,
  titleClassName,
  subTitleClassName,
  showTooltip = false,
  footer,
  dropdownClassName,
  hideDisabledOptions = false,
  optionClassName,
  showOptionTooltip = false,
}: SelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [visibleOptions, setVisibleOptions] =
    useState<OptionNameValuePair<T>[]>(options);
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const [tooltipPortal, setTooltipPortal] = useState<{
    text: string;
    style: React.CSSProperties;
  } | null>(null);

  const disabledOptionClassName = `cursor-not-allowed opacity-50`;

  const renderOptionLabel = (option?: OptionNameValuePair<T>) => {
    if (!option) return null;
    return option.display_component ?? option.name;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropdownMenuRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const updateMenuPosition = () => {
    const anchor = dropdownRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();

    const estimatedMenuHeight = enableSearch ? 320 : 260; 
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlipToTop =
      (position === "bottom" && spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) ||
      (position === "top" && spaceAbove >= estimatedMenuHeight);

    // We keep existing dropdown classNames; only positioning is computed here.
    const base: React.CSSProperties = {
      position: "fixed",
      left: rect.left,
      zIndex: 9999,
      minWidth: rect.width,
    };

    if (shouldFlipToTop) {
      // Equivalent to "bottom-full mb-1": place above trigger with a small gap.
      base.top = rect.top - 4;
      base.transform = "translateY(-100%)";
    } else {
      // Equivalent to "top-full mt-1": place below trigger with a small gap.
      base.top = rect.bottom + 4;
    }

    setMenuStyle(base);
  };

  const updateTooltipPosition = (text: string) => {
    const anchor = dropdownRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setTooltipPortal({
      text,
      style: {
        position: "fixed",
        left: rect.left,
        top: rect.bottom + 4,
        zIndex: 9999,
      },
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, position, dropdownClassName, options.length, selectedOption?.value]);

  useEffect(() => {
    const base = hideDisabledOptions
      ? options.filter(
          (opt) =>
            !opt.disabled ||
            (selectedOption && opt.value === selectedOption.value)
        )
      : options;
    setVisibleOptions(base);
  }, [options, hideDisabledOptions, selectedOption]);

  const toggleIsOpen = () => {
    if (disabled) return;
    setIsOpen((current) => {
      const next = !current;
      if (next) {
        // Position immediately on open for a snappy feel.
        queueMicrotask(() => updateMenuPosition());
      }
      return next;
    });
  };

  const filterOptions = (event: ChangeEvent<HTMLInputElement>) => {
    const base = hideDisabledOptions
      ? options.filter(
          (opt) =>
            !opt.disabled ||
            (selectedOption && opt.value === selectedOption.value)
        )
      : options;
    const filteredOptions = base.filter((option) =>
      option.name.toLowerCase().startsWith(event.target.value.toLowerCase())
    );
    setVisibleOptions(filteredOptions);
  };

  const onSelect = (option: OptionNameValuePair<T>) => {
    if (option.disabled) return;
    onSelectionChange(option);
    toggleIsOpen();
  };

  const renderTooltip = (text: string) => (
    tooltipPortal &&
    ReactDOM.createPortal(
      <div
        className="absolute z-50 left-0 top-full mt-1 bg-gray-700 rounded-md shadow-lg whitespace-pre-wrap break-words font-sans font-normal text-xs leading-[1rem]"
        style={{
          ...tooltipPortal.style,
          color: "#FAFAFA",
          width: "146px",
          maxWidth: "384px",
          paddingTop: "0.375rem",
          paddingRight: "0.75rem",
          paddingBottom: "0.375rem",
          paddingLeft: "0.75rem",
          gap: "8px",
          opacity: 1,
        }}
      >
        {text}
        <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-700 transform rotate-45" />
      </div>,
      document.body
    )
  );

  const renderTextWithTooltip = (
    text: string,
    tooltipKey: string,
    className: string
  ) => (
    <div
      className="relative"
      onMouseEnter={() => {
        if (!showTooltip) return;
        setHoveredTooltip(tooltipKey);
        updateTooltipPosition(text);
      }}
      onMouseLeave={() => {
        if (!showTooltip) return;
        setHoveredTooltip(null);
        setTooltipPortal(null);
      }}
    >
      <span className={className}>{text}</span>
      {!isOpen &&
        showTooltip &&
        hoveredTooltip === tooltipKey &&
        renderTooltip(text)}
    </div>
  );

  const renderSelectedTitle = (option: OptionNameValuePair<T>) => {
    if (option.display_component) {
      return (
        <div className={cn("flex items-center", titleClassName)}>
          {option.display_component}
        </div>
      );
    }

    return renderTextWithTooltip(
      option.name,
      "title",
      cn(
        "font-sans font-medium text-sm leading-5 align-middle text-[#0A0A0A] truncate block max-w-full",
        titleClassName
      )
    );
  };

  const renderSelectedValue = (option: OptionNameValuePair<T>) => {

      if (option.selected_display_component) {
    return (
      <div
        className={cn(
          "flex flex-1 min-w-0 items-center",
          titleClassName,
        )}
      >
        {option.selected_display_component}
      </div>
    );
  }
    if (option.display_component) {
      return (
        <div className={cn("flex items-center", titleClassName)}>
          {option.display_component}
        </div>
      );
    }
    return renderTextWithTooltip(
      option.name,
      "name",
      cn("text-sm text-gray-900 truncate block", titleClassName)
    );
  };

  return (
    <div
      ref={dropdownRef}
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      title={showOptionTooltip && selectedOption ? selectedOption.name : undefined}
      className={cn(
        "relative",
        containerClassName,
        isFocused && focusClassName
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between w-full rounded cursor-pointer",
          labelClassName,
          selectedOption && selectedContainerClassName,
        )}
        onClick={toggleIsOpen}
      >
        {showTitleSubTitle ? (
          selectedOption ? (
            <div className="flex flex-col">
              {renderSelectedTitle(selectedOption)}
              {selectedOption.subTitle &&
                renderTextWithTooltip(
                  selectedOption.subTitle,
                  "subtitle",
                  cn(
                    "overflow-hidden text-ellipsis whitespace-nowrap font-sans text-sm font-normal leading-5 text-[#737373]",
                    subTitleClassName
                  )
                )}
            </div>
          ) : (
            <span
              className={cn(
                "font-sans font-normal text-sm leading-5 align-middle text-[#737373]",
                titleClassName
              )}
            >
              {name}
            </span>
          )
        ) : selectedOption ? (
            <div className="flex-1 min-w-0">
              {renderSelectedValue(selectedOption)}
            </div>
        ) : (
          <span className="font-normal text-sm leading-5 text-[#737373] truncate min-w-0 flex-1 text-left">Select {name}</span>
        )}
        {!disabled &&
          !hideChevron &&
          (isOpen ? (
            <ChevronUp className="ml-2 h-4 shrink-0 text-gray-500" />
          ) : (
            <ChevronDown className="ml-2 h-4 shrink-0 text-gray-500" />
          ))}
      </div>
      {isOpen &&
        menuStyle &&
        ReactDOM.createPortal(
          <div
            ref={dropdownMenuRef}
            style={menuStyle}
            className={cn(
              // Keep legacy width behavior (`min-w-full w-max`) and spacing classes.
              // Positioning is handled via the fixed `menuStyle`.
              "absolute left-0 z-11 mt-1 w-max min-w-full bg-white border border-gray-300 rounded-md shadow-overviewCard max-h-60 overflow-auto",
              dropdownClassName
            )}
          >
            {enableSearch && (
              <div className="sticky top-0 bg-white p-1">
                <div className="relative">
                  <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
                  <input
                    className="w-full pl-7 pr-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder={
                      placeholder || `Search ${name.toLocaleLowerCase()}`
                    }
                    type="text"
                    onChange={filterOptions}
                  />
                </div>
              </div>
            )}
            <div className="p-1 pb-2 space-y-1">
              {visibleOptions.length === 0 && (
                <div className="px-4 py-1.5 text-sm text-[#737373]">
                  No options
                </div>
              )}
              {visibleOptions.map((option) =>
                selectedOption && option.value === selectedOption.value ? (
                  <div
                    key={option.name}
                    title={showOptionTooltip ? option.name : undefined}
                    className={cn(
                      "rounded-md text-sm px-4 py-1.5 flex justify-between items-center hover:bg-gray-100 bg-slate-200",
                      optionClassName
                    )}
                  >
                    <span className="whitespace-nowrap">
                      {renderOptionLabel(option)}
                    </span>
                    <span className="ml-2">
                      <Check className="h-4 w-4 text-gray-600" />
                    </span>
                  </div>
                ) : (
                  <div
                    key={option.name}
                    title={showOptionTooltip ? option.name : undefined}
                    className={cn(
                      "rounded-md text-sm px-4 py-1.5 cursor-pointer hover:bg-gray-100 whitespace-nowrap",
                      optionClassName,
                      option.disabled && disabledOptionClassName
                    )}
                    onClick={() => {
                      if (option.disabled) return;
                      onSelect(option);
                    }}
                  >
                    {renderOptionLabel(option)}
                  </div>
                )
              )}
            </div>
            {footer && footer}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SelectDropdownV2;
